<template>
    <!--Page alert + loader-->
    <PageStatus :alert="defaultAlert" :table-data="tableData" />

    <!--Filter Data Modal (shared: FilterModal.vue)-->
    <FilterModal
        :draft="filterDraft"
        v-model:search-by="tableData.searchBy"
        :view-options="viewOptions"
        :search-by-options="searchByOptions"
        :alert="defaultAlert"
        @search-by-change="changePlaceholder()"
        @apply="applyFilters"
        @clear="clearFilters"
    >
        <!-- Type: one or more (uses the shared "statuses" filter) -->
        <SelectField
            v-model="filterDraft.statuses"
            col="mb-3"
            label="Type"
            icon="fa fa-cube"
            class="multiSelect-validation-field"
            :options="statusOptions"
            :reduce="option => option.value"
            :close-on-select="false"
            placeholder="All types"
            multiple
        />
    </FilterModal>
    <!--End Filter Data Modal-->

<!--Begin form-->
<RecordFormModal
    :title="isEditing ? 'Update Module' : 'Add Module'"
    title-icon="fa fa-cubes"
    :is-editing="isEditing"
    :is-saving="isSaving"
    :alert="defaultAlert"
    @save="saveOrEditRecord"
    @sample="addSampleData"
    @reset="resetForm"
>
    <HiddenField v-model="formInputs.id" />

    <!-- Row 1: Key & Name -->
    <div class="row g-3 mb-0">
        <FormField
            v-model="formInputs.module_key"
            col="col-md-6"
            label="Module Key"
            icon="fa fa-key"
            placeholder="e.g. pos"
            :disabled="isEditing"
            :tooltip="isEditing ? 'The key cannot be changed after the module is created' : 'Small letters, numbers and _ only, e.g. pos or hr_payroll'"
            required
            @input="handleInputChange"
            @change="handleInputChange"
        />
        <FormField
            v-model="formInputs.module_name"
            col="col-md-6"
            label="Module Name"
            icon="fa fa-cube"
            placeholder="e.g. Point of Sale"
            tooltip="The name people see on the Apps page and at sign-up"
            required
            @input="handleInputChange"
            @change="handleInputChange"
        />
    </div>

    <!-- Row 2: Type & Order -->
    <div class="row g-3 mb-0">
        <SelectField
            v-model="formInputs.module_type"
            col="col-md-6"
            name="module_type"
            label="Type"
            icon="fa fa-sitemap"
            :options="statusOptions"
            :reduce="option => option.value"
            :disabled="isProtectedModule(formInputs)"
            placeholder="Select type"
            tooltip="Selectable: companies switch it on or off. Always on: every company has it. Platform: your company only."
            :submitted="submitted"
            required
            @change="$clearValidationAlert('#recordForm')"
        />
        <FormField
            v-model="formInputs.sort_order"
            col="col-md-6"
            type="number"
            label="Order"
            icon="fa fa-sort-numeric-asc"
            min="0"
            placeholder="e.g. 10"
            tooltip="Smaller numbers come first on the Apps page and at sign-up"
            required
            @input="handleInputChange"
            @change="handleInputChange"
        />
    </div>

    <!-- Row 3: Icon & Permission prefixes -->
    <div class="row g-3 mb-0">
        <FormField
            v-model="formInputs.icon"
            col="col-md-6"
            label="Icon"
            icon="fa fa-picture-o"
            placeholder="e.g. fa fa-shopping-cart"
            tooltip="A Font Awesome 4 icon class"
            @input="handleInputChange"
            @change="handleInputChange"
        />
        <FormField
            v-model="formInputs.permission_prefixes"
            col="col-md-6"
            label="Permission Prefixes"
            icon="fa fa-shield"
            placeholder="e.g. pos_"
            tooltip="Permissions starting with these are hidden when the module is off. Separate with commas; each ends with _ or ."
            @input="handleInputChange"
            @change="handleInputChange"
        />
    </div>

    <!-- Row 4: Description -->
    <div class="row g-3 mb-0">
        <TextareaField
            v-model="formInputs.description"
            col="col-12"
            label="Description"
            icon="fa fa-align-left"
            rows="2"
            maxlength="255"
            tooltip="One short line shown on the Apps page and at sign-up (up to 255 characters)"
            @input="handleInputChange"
        />
    </div>

    <div class="card-header bg-sa-card-header d-flex justify-content-between align-items-center">
        <span><i class="bi bi-ui-checks me-2"></i>Options</span>
    </div>

    <!-- Row 5: Default & Active -->
    <div class="row g-3 mb-3">
        <CheckField
            v-model="formInputs.is_default"
            col="col-md-6"
            label="Default"
            text="Switched on for new companies that pick nothing at sign-up"
            :disabled="formInputs.module_type !== 'selectable'"
            @change="handleInputChange"
        />
        <CheckField
            v-model="formInputs.is_active"
            col="col-md-6"
            label="Active"
            text="Show this module on the Apps page and at sign-up"
            :disabled="isProtectedModule(formInputs)"
            @change="handleInputChange"
        />
    </div>
</RecordFormModal>
<!--End form-->


       <h4 class="sa-core-color fw-semibold mb-2"><i class="fa me-2 opacity-75" :class="pageIcon"></i>{{ pageTitle }}</h4>


<!--Tables-->
<div class="card glass mt-0 bg-sa-card">
    <div class="card-body bg-sa-card-body p-0">

        <!-- Toggleable Tabs (one tab per type, same page) -->
        <ul class="nav nav-tabs app-tabs" role="tablist">
            <li class="nav-item" role="presentation">
                <a
                    href="#"
                    class="nav-link"
                    :class="{ 'app-tab-active': activeTab === 'all' }"
                    @click.prevent="selectStatusTab('all')"
                >
                    <span class="sa-core-color">All</span>
                </a>
            </li>
            <li v-for="option in statusOptions" :key="option.value" class="nav-item" role="presentation">
                <a
                    href="#"
                    class="nav-link"
                    :class="{ 'app-tab-active': activeTab === option.value }"
                    @click.prevent="selectStatusTab(option.value)"
                >
                    <span class="sa-core-color">{{ option.label }}</span>
                </a>
            </li>
        </ul>
        <!-- End Toggleable Tabs -->

        <!-- Table action row (shared: TableToolbar.vue) -->
        <TableToolbar
            :can-add="$hasPermission('platform_modules.add')"
            :filter-count="activeFilterCount"
            :columns="tableColumns"
            v-model:hidden-columns="hiddenColumns"
            :selected-count="tableData.selectedRecordIds.length"
            :deleted-view="tableData.recordFilter === 'deletedone'"
            :can-delete="$hasPermission('platform_modules.delete')"
            @add="clearForm()"
            @filter="openFilters"
            @clear-selection="clearSelection"
            @bulk-action="runBulkAction"
        >
            <!-- More menu -->
            <template #more>
                        <li><h6 class="dropdown-header">Data</h6></li>
                        <li>
                            <button type="button" class="dropdown-item" @click="exportRecords('xls')">
                                <i class="fa fa-file-excel-o fa-fw me-1 text-success"></i> Export to Excel
                            </button>
                        </li>
                        <li>
                            <button type="button" class="dropdown-item" @click="exportRecords('csv')">
                                <i class="fa fa-file-text-o fa-fw me-1 text-success"></i> Export to CSV
                            </button>
                        </li>
                        <li>
                            <button type="button" class="dropdown-item" @click="exportRecords('pdf')">
                                <i class="fa fa-file-pdf-o fa-fw me-1 text-danger"></i> Export to PDF
                            </button>
                        </li>
            </template>
        </TableToolbar>
        <!-- Table Search Panel -->
        <SearchBox
            v-model:search-query="tableData.searchQuery"
            v-model:records-per-page="tableData.recordsPerPage"
            :placeholder="tableData.searchPlaceHolderText"
            @search-input="onSearchInput"
            @search-click="onSearchButtonClick"
            @per-page-change="onRecordsPerPageChange"
        />
        <!-- What the table is currently showing -->
        <ActiveFilters
            :chips="activeFilterChips"
            @remove="removeFilterChip"
            @clear="clearAllFilterChips"
        />
        <!-- Table -->
        <div class="table-responsive">
            <table
                class="table table-hover align-middle mb-0"
                id="bootstrapTable"
                tabindex="0"
                @keydown.up="$moveRowSelection(-1, $event)"
                @keydown.down="$moveRowSelection(1, $event)"
                @keydown.enter="$onTableEnter($event)">
                <thead>
                    <tr>
                        <th>
                            <input type="checkbox" v-model="tableData.selectAll" @change="toggleSelectAll" />
                        </th>
                        <th class="hide-sm-td"><span class="visually-hidden">Details</span></th>
                        <th class="button hide-sm-td">No</th>
                        <SortableTh v-if="isColumnVisible('module_name')" column="module_name" :table-data="tableData" @sort="orderBy">Name</SortableTh>
                        <SortableTh v-if="isColumnVisible('module_key')" column="module_key" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Key</SortableTh>
                        <SortableTh v-if="isColumnVisible('module_type')" column="module_type" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Type</SortableTh>
                        <SortableTh v-if="isColumnVisible('is_default')" column="is_default" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Default</SortableTh>
                        <SortableTh v-if="isColumnVisible('is_active')" column="is_active" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Active</SortableTh>
                        <SortableTh v-if="isColumnVisible('sort_order')" column="sort_order" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Order</SortableTh>
                        <SortableTh v-if="isColumnVisible('date_created')" column="date_created" :table-data="tableData" @sort="orderBy" class="hide-sm-td">Date</SortableTh>
                        <th class="button">Action</th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="(record, index) in tableData.records" :key="record.id">
                        <tr class="clickable-row"
                            :data-record-id="record.id"
                            :class="{ 'active-row main-row': tableData.selectedRecordId === record.id, 'row-just-saved': isJustSaved(record) }"
                            @click="$onRowClick(record, $event)"
                            @dblclick="$onRowDblClick(record, $event)"
                        >
                            <td>
                                <input type="checkbox" v-model="tableData.selectedRecordIds" :value="record.id" />
                            </td>
                            <td class="expand-cell hide-sm-td"
                                :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                                @click="$toggleDetails(record.id)">
                                <i
                                class="fa fa-lg expand-icon ms-1"
                                :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle'"
                            ></i>
                            </td>
                            <td class="hide-sm-td">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}</td>
                            <td v-if="isColumnVisible('module_name')">
                            <!-- Phones: + / − and the number go in front of the name (their columns are hidden) -->
                            <span
                                class="show-sm-inline expand-cell me-2"
                                :aria-expanded="expandedRows[record.id] ? 'true' : 'false'"
                                @click="$toggleDetails(record.id)"
                            >
                                <i
                                    class="fa fa-lg expand-icon"
                                    :class="expandedRows[record.id] ? 'fa-minus-circle text-danger' : 'fa-plus-circle'"
                                ></i>
                            </span>
                            <span class="show-sm-inline">{{ (tableData.currentPage - 1) * tableData.recordsPerPage + index + 1 }}. </span>
                            <i v-if="record.icon" :class="record.icon" class="fa-fw me-1 opacity-75"></i>{{ record.module_name }}
                            </td>
                            <td v-if="isColumnVisible('module_key')" class="hide-sm-td"><code>{{ record.module_key }}</code></td>
                            <td v-if="isColumnVisible('module_type')" class="hide-sm-td">
                                <StatusBadge :status="record.module_type" :options="statusOptions" />
                            </td>
                            <td v-if="isColumnVisible('is_default')" class="hide-sm-td">
                                <StatusBadge :status="String(record.is_default)" :options="yesNoOptions" />
                            </td>
                            <td v-if="isColumnVisible('is_active')" class="hide-sm-td">
                                <StatusBadge :status="String(record.is_active)" :options="yesNoOptions" />
                            </td>
                            <td v-if="isColumnVisible('sort_order')" class="hide-sm-td">{{ record.sort_order }}</td>
                            <td v-if="isColumnVisible('date_created')" class="hide-sm-td text-nowrap" :title="$ConvertToLocalDate(record.date_created)">{{ $friendlyDate(record.date_created) }}</td>
                            <td>
                                <!--Delete Modal Part-->
                                <RowActions
                                    :record="record"
                                    :modal-id="'deletePlatformModuleModal' + record.id"
                                    :can-edit="$hasPermission('platform_modules.add')"
                                    :can-soft-delete="$hasPermission('platform_modules.delete') && !isProtectedModule(record)"
                                    :can-restore="$hasPermission('platform_modules.delete')"
                                    :can-delete-permanently="$hasPermission('platform_modules.delete') && !isProtectedModule(record)"
                                    @edit="editRecord"
                                    @delete-type="setDeleteType"
                                />
                                <DeleteModal
                                    :modal-id="'deletePlatformModuleModal' + record.id"
                                    :title="deleteModalTitle"
                                    :message="deleteModalMessage"
                                    @confirm="deleteRecord(record.id)"
                                />
                                <!--End Delete Part Modal-->
                            </td>
                        </tr>
                        <!-- Hidden expanded row -->
                        <tr class="collapse-row">
                            <td class="p-0"></td>
                            <td class="p-0 hide-sm-td"></td>
                            <td colspan="9" class="p-0">
                                <div :id="'tableDetails'+ record.id" class="collapse table-collapse table-details">

                                    <div class="p-3 details-list">
    <div><i class="fa fa-hashtag"></i><strong>Module Number:</strong> {{ record.module_number }}</div>

    <!-- Shown on phones, or always when the column is hidden with the Columns button -->
    <div :class="{ 'show-sm-td': isColumnVisible('module_name') }"><i class="fa fa-cube"></i><strong>Name:</strong> {{ record.module_name }}</div>
    <div :class="{ 'show-sm-td': isColumnVisible('module_key') }"><i class="fa fa-key"></i><strong>Key:</strong> <code>{{ record.module_key }}</code></div>
    <div :class="{ 'show-sm-td': isColumnVisible('module_type') }"><i class="fa fa-sitemap"></i><strong>Type:</strong> <StatusBadge :status="record.module_type" :options="statusOptions" /></div>
    <div :class="{ 'show-sm-td': isColumnVisible('is_default') }"><i class="fa fa-check-square-o"></i><strong>Default:</strong> <StatusBadge :status="String(record.is_default)" :options="yesNoOptions" /></div>
    <div :class="{ 'show-sm-td': isColumnVisible('is_active') }"><i class="fa fa-toggle-on"></i><strong>Active:</strong> <StatusBadge :status="String(record.is_active)" :options="yesNoOptions" /></div>
    <div :class="{ 'show-sm-td': isColumnVisible('sort_order') }"><i class="fa fa-sort-numeric-asc"></i><strong>Order:</strong> {{ record.sort_order }}</div>

    <div><i class="fa fa-picture-o"></i><strong>Icon:</strong> <i v-if="record.icon" :class="record.icon" class="fa-fw me-1"></i>{{ record.icon }}</div>
    <div><i class="fa fa-shield"></i><strong>Permission Prefixes:</strong> {{ record.permission_prefixes }}</div>
    <div><i class="fa fa-align-left"></i><strong>Description:</strong> <span class="text-pre-line">{{ record.description }}</span></div>

    <div><i class="fa fa-user"></i><strong>Created By:</strong> {{ [record.user_first_name, record.user_middle_name].filter(Boolean).join(' ') }}</div>
    <div :class="{ 'show-sm-td': isColumnVisible('date_created') }"><i class="fa fa-calendar"></i><strong>Date Created:</strong> {{ $ConvertToLocalDate(record.date_created) }}</div>
    <div><i class="fa fa-edit"></i><strong>Updated By:</strong> {{ record.updatedby_firstname }}</div>
    <div><i class="fa fa-calendar"></i><strong>Date Updated:</strong> {{ $ConvertToLocalDate(record.date_updated) }}</div>
    <div v-if="$hasPermission('view-ip-address')"><i class="fa fa-desktop"></i><strong>IP Address:</strong> {{ record.ip_address }}</div>
</div>

                                </div>
                            </td>
                        </tr>
                    </template>
                    <!-- No rows: blank while loading, then the empty message in the middle -->
                    <tr v-if="tableData.records.length === 0" class="table-filler">
                        <td colspan="11" class="text-center align-middle" :style="{ height: fullPageHeight() }">
                            <TableEmptyState
                                v-if="!tableLoading"
                                :has-filters="activeFilterCount > 0 || !!tableData.appliedSearchQuery"
                                :can-add="$hasPermission('platform_modules.add')"
                                @add="clearForm()"
                                @clear="clearAllFilterChips"
                            />
                        </td>
                    </tr>
                </tbody>
                <tfoot class="table-light">
                    <tr>
                        <td colspan="11">
                            <!--Pagination (hidden when there are no rows; its space is kept)-->
                            <div v-if="tableData.records.length === 0 && tableData.currentPage <= 1" class="pagination-placeholder"></div>
                            <Pagination
                                v-else
                                :currentPage="tableData.currentPage"
                                :totalPages="tableData.totalPages"
                                :hasNextPage="tableData.hasNextPage"
                                :totalRecords="tableData.totalRecords"
                                :startRecord="tableData.startRecord"
                                :endRecord="tableData.endRecord"
                                :showFullPagination="tableData.totalRecords > 0"
                                :lastKnownPage="lastKnownPage"
                                @page-changed="fetchRecords"
                                @loaderMessage="updateLoaderMessage"
                                @last-page-request="goToLastPage"
                            />
                            <!--End Pagination-->
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    </div>
</div>
<!--End Tables-->


</template>
<script>
    /*
     * Platform → Modules: the list of ERP modules (modules table).
     * Built from CrudOnlineOnly.vue. Platform company only.
     * The "statuses" filter, tabs and badges hold the module TYPE here.
     */
    import "vue-select/dist/vue-select.css";

    import PageStatus from "../../../components/admin/PageStatus.vue";
    import TableToolbar from "../../../components/admin/TableToolbar.vue";
    import Pagination from "../../../components/admin/Pagination.vue";
    import TableEmptyState from "../../../components/admin/TableEmptyState.vue";
    import SortableTh from "../../../components/admin/SortableTh.vue";
    import DeleteModal from "../../../components/admin/DeleteModal.vue";
    import RowActions from "../../../components/admin/RowActions.vue";
    import SearchBox from "../../../components/admin/SearchBox.vue";
    import RecordFormModal from "../../../components/admin/RecordFormModal.vue";
    import FormField from "../../../components/admin/FormField.vue";
    import SelectField from "../../../components/admin/SelectField.vue";
    import FilterModal from "../../../components/admin/FilterModal.vue";
    import ActiveFilters from "../../../components/admin/ActiveFilters.vue";
    import StatusBadge from "../../../components/admin/StatusBadge.vue";
    import HiddenField from "../../../components/admin/HiddenField.vue";
    import CheckField from "../../../components/admin/CheckField.vue";
    import TextareaField from "../../../components/admin/TextareaField.vue";
    import platformModulesMixin from "./mixins/platformModulesMixin.js";
    import tableMixin from "../../../mixins/tableMixin.js";
    import axios from "axios";

    export default {
        name: "PlatformModules",
        mixins: [tableMixin, platformModulesMixin], // Inject all methods here
        components: {
            PageStatus,
            TableToolbar,
            Pagination,
            TableEmptyState,
            SortableTh,
            DeleteModal,
            RowActions,
            SearchBox,
            RecordFormModal,
            FormField,
            SelectField,
            FilterModal,
            ActiveFilters,
            StatusBadge,
            HiddenField,
            CheckField,
            TextareaField,
        },
        data() {
            return {
                // Standard data (isSaving, tableData, defaultAlert ...) comes from tableMixin
                standardPageSetup: true,          // tableMixin sets up Enter-to-save and table shortcuts
                pageIcon: "fa-cubes",
                formInputs: this.getDefaultFormInputs(),
                apiUrl: "/subscription/api_platform_modules.php", // used by tableMixin's fetchRecords, delete, export
                exportFileName: "platformModulesExport", // name of the exported file
                columnsKey: "platform_modules",          // saves this page's hidden columns (Columns button)
                extraFilterKeys: ["statuses"],           // module type filter in the Filter window
                // Filter window: View
                viewOptions: [
                    { label: "All Records", value: "all" },
                    { label: "Records By Me", value: "byme" },
                    { label: "Records Deleted", value: "deletedone" },
                ],
                // Filter window: Search By
                searchByOptions: [
                    { label: "Name", value: "module_name" },
                    { label: "Key", value: "module_key" },
                    { label: "Module Number", value: "module_number" },
                    { label: "Permission Prefix", value: "permission_prefixes" },
                ],
                // Module types (same list as moduleTypes() in api_platform_modules_helpers.php)
                statusOptions: [
                    { label: "Selectable", value: "selectable", color: "primary" },
                    { label: "Always on", value: "always_on", color: "success" },
                    { label: "Platform", value: "platform", color: "dark" },
                ],
                yesNoOptions: [
                    { label: "Yes", value: "1", color: "success" },
                    { label: "No", value: "0", color: "secondary" },
                ],
            };
        },
        async created() {
            await this.fetchRecords(
                this.tableData.currentPage,
                this.tableData.currentSortColumn,
                "ASC",
                this.tableData.startDate,
                this.tableData.endDate,
                this.$loaderMessages.loaderMessageDefault,
            );
        },
        computed: {
            // Which type tab is highlighted, based on the current type filter
            activeTab() {
                const statuses = this.tableData.statuses || [];
                if (statuses.length === 0) return "all";
                if (statuses.length === 1) return statuses[0];
                return null; // several types chosen in the Filter window: no tab highlighted
            },
            // Page heading that follows the chosen filter.
            pageTitle() {
                const titles = {
                    all: "Modules",
                    byme: "Modules By Me",
                    deletedone: "Modules Deleted",
                };
                return titles[this.tableData.recordFilter] || "Modules";
            },
            // Columns the user can show or hide (Columns button)
            tableColumns() {
                return [
                    { key: "module_name", label: "Name" },
                    { key: "module_key", label: "Key" },
                    { key: "module_type", label: "Type" },
                    { key: "is_default", label: "Default" },
                    { key: "is_active", label: "Active" },
                    { key: "sort_order", label: "Order" },
                    { key: "date_created", label: "Date" },
                ];
            },
        },
        methods: {
            // Type tabs: set the type filter and reload from page 1
            selectStatusTab(status) {
                this.tableData.statuses = status === "all" ? [] : [status];
                this.tableData.currentPage = 1;
                this.fetchRecords(
                    1,
                    this.tableData.currentSortColumn,
                    "ASC",
                    this.tableData.startDate,
                    this.tableData.endDate,
                    this.$loaderMessages.loaderMessageDefault,
                );
            },
            getDefaultFormInputs() {
                return {
                    id: "",
                    module_key: "",
                    module_name: "",
                    module_type: "selectable",
                    sort_order: "",
                    icon: "",
                    permission_prefixes: "",
                    description: "",
                    is_default: false,
                    is_active: true,
                };
            },
            // Table defaults: the standard ones (tableMixin) + this page's own
            getDefaultTableData() {
                return {
                    ...this.baseTableData(),
                    currentSortColumn: "sort_order",
                    searchBy: "module_name",
                    searchPlaceHolderText: "Search by name",
                    statuses: [],   // type filter (empty = all)
                };
            },
            saveOrEditRecord() {
                // Ignore the call if a save is already running.
                if (this.isSaving) return;
                this.isSaving = true;

                try {
                    this.$scrollToTopModal("recordFormModal");
                    this.submitted = true;
                    const formIsValid = this.$validateForm("#recordForm");
                    if (!formIsValid) {
                        this.$playErrorSound();
                        this.handleAlert("starting", "Please correct the highlighted fields.", "danger", "modal");
                        this.isSaving = false;
                        return;
                    }
                    this.handleAlert("starting", "Please wait submiting", "info", "modal");
                    const formData = this.formData_fields();
                    // New records should appear on the first page.
                    if (!this.formInputs.id) {
                        this.tableData.currentPage = 1;
                    }
                    axios
                        .post(this.apiUrl, formData, this.$handleConfig())
                        .then((response) => {
                            if (response.data.status == "success") {
                                this.handleSaveOrEditSuccess(response);
                            } else {
                                this.handleError("starting", response.data.message[0], "danger", "modal");
                            }
                        })
                        .catch((error) => {
                            this.handleRequestError(error, "modal");
                        })
                        .finally(() => {
                            this.isSaving = false;
                        });
                } catch (error) {
                    this.isSaving = false;
                    throw error;
                }
            },
            formData_fields() {
                const formData = new FormData();
                formData.append("id", this.formInputs.id);
                formData.append("module_key", this.formInputs.module_key || "");
                formData.append("module_name", this.formInputs.module_name || "");
                formData.append("module_type", this.formInputs.module_type || "");
                formData.append("sort_order", this.formInputs.sort_order ?? "");
                formData.append("icon", this.formInputs.icon || "");
                formData.append("permission_prefixes", this.formInputs.permission_prefixes || "");
                formData.append("description", this.formInputs.description || "");
                formData.append("is_default", this.formInputs.is_default ? 1 : 0);
                formData.append("is_active", this.formInputs.is_active ? 1 : 0);
                formData.append("action", "saveOrEditRecord");
                return formData;
            },
            clearForm() {
                this.isSaving = false;
                this.isEditing = false;
                this.formInputs = this.getDefaultFormInputs();
                this.formInputs.sort_order = this.nextSortOrder();
                this.$clearValidationStates("#recordForm");
            },
            editRecord(records) {
                this.isSaving = false;
                this.isEditing = true;
                this.formInputs = {
                    id: records.id ?? "",
                    module_key: records.module_key ?? "",
                    module_name: records.module_name ?? "",
                    module_type: records.module_type ?? "selectable",
                    sort_order: records.sort_order ?? "",
                    icon: records.icon ?? "",
                    permission_prefixes: records.permission_prefixes ?? "",
                    description: records.description ?? "",
                    is_default: Number(records.is_default) === 1,
                    is_active: Number(records.is_active) === 1,
                };
                this.$clearValidationStates("#recordForm");
            },

            // fetchRecords, goToLastPage, deleteRecord, deleteSelectedRecords,
            // exportRecords: in tableMixin (uses apiUrl)
        },
    };
</script>